import { UserInfo } from "@/store/user/userSlice"
import axios from "axios"
import { IntegrationType } from "types"
import { typedEnv } from "../../config/typedEnv"

export enum ActivatedOpinionPopup {
  OPINION_AFTER_INITIAL_SETUP = "OPINION_AFTER_INITIAL_SETUP",
}

class OpinionService {
  private readonly axiosInstances: Map<IntegrationType, ReturnType<typeof axios.create>> = new Map()

  async completeInitialSetup(userInfo: UserInfo, productType?: string): Promise<void> {
    await this.getAxiosInstance(userInfo.integrationType).post(
      `shop/${userInfo.username.split("-")[0]}/opinion/initial-setup`,
      { productType }
    )
  }

  async dismissPopup(params: {
    userInfo: UserInfo
    popupType: ActivatedOpinionPopup
  }): Promise<void> {
    const { userInfo, popupType } = params
    await this.getAxiosInstance(userInfo.integrationType).post(
      `shop/${userInfo.username.split("-")[0]}/opinion/popup/dismiss`,
      { popupType }
    )
  }

  async submitOpinion(params: {
    userInfo: UserInfo
    popupType: ActivatedOpinionPopup
    rating: number
    description?: string
  }): Promise<void> {
    const { userInfo, popupType, rating, description } = params
    await this.getAxiosInstance(userInfo.integrationType).post(
      `shop/${userInfo.username.split("-")[0]}/opinion`,
      {
        popupType,
        rating,
        description,
      }
    )
  }

  async getCurrentPopup(params: { userInfo: UserInfo }): Promise<ActivatedOpinionPopup | null> {
    const { userInfo } = params
    const response = await this.getAxiosInstance(userInfo.integrationType).get(
      `shop/${userInfo.username.split("-")[0]}/opinion/popup/current`
    )
    return response.data.popup || null
  }

  isEnabled(integrationType: IntegrationType) {
    return integrationType === IntegrationType.TIENDANUBE
  }

  private getBaseUrl(integrationType: IntegrationType): string {
    switch (integrationType) {
      case IntegrationType.TIENDANUBE:
        return typedEnv.VITE_TIENDANUBE_API_URL
      default:
        return undefined
    }
  }

  private getAxiosInstance(integrationType: IntegrationType) {
    if (this.getBaseUrl(integrationType) === undefined)
      return {
        get: () => Promise.resolve({ data: { popup: null } }),
        post: () => Promise.resolve(),
      }

    if (!this.axiosInstances.has(integrationType))
      this.axiosInstances.set(
        integrationType,
        axios.create({
          baseURL: this.getBaseUrl(integrationType),
          headers: {
            "Content-Type": "application/json",
          },
        })
      )

    return this.axiosInstances.get(integrationType)!
  }
}

export const opinionService = new OpinionService()
